import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { auth } from '@/config/firebase'
import { useToast } from '@/context/ToastContext'
import {
  AccountService,
  FirstLoginPasswordChangeError,
} from '@common/services/account-service'
import { PASSWORD_REQUIREMENT_MESSAGE } from '@common/utils/password-validator'

export const useChangePassword = () => {
  const navigate = useNavigate()
  const { showSuccessMessage, showErrorMessage } = useToast()

  const [initialPassword, setInitialPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showInitialPassword, setShowInitialPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [initialPasswordError, setInitialPasswordError] = useState<string | null>(null)
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null)
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setInitialPasswordError(null)
    setNewPasswordError(null)
    setConfirmPasswordError(null)

    const accountService = new AccountService()
    const validation = accountService.validateFirstLoginPasswordChange({
      initialPassword,
      newPassword,
      confirmPassword,
    })
    if (!validation.ok) {
      setInitialPasswordError(validation.errors.initialPassword ?? null)
      setNewPasswordError(validation.errors.newPassword ?? null)
      setConfirmPasswordError(validation.errors.confirmPassword ?? null)
      return
    }

    setIsLoading(true)

    try {
      await accountService.changePasswordOnFirstLogin({
        auth,
        user: auth.currentUser,
        accountCollection: 'admin_accounts',
        initialPassword: validation.trimmed.initialPassword,
        newPassword: validation.trimmed.newPassword,
      })
      showSuccessMessage('パスワードを更新しました。再度ログインしてください。')
      navigate('/login')
    } catch (err: unknown) {
      console.error('Password change error:', err)
      const code = err instanceof FirstLoginPasswordChangeError ? err.code : null
      if (code === 'wrong-password') {
        const errorMsg = '初回ログインパスワードが正しくありません'
        setInitialPasswordError(errorMsg)
        showErrorMessage(errorMsg)
      } else if (code === 'weak-password') {
        setNewPasswordError(PASSWORD_REQUIREMENT_MESSAGE)
        showErrorMessage('パスワードが弱すぎます')
      } else if (code === 'requires-recent-login') {
        showErrorMessage('セキュリティのため、再度ログインしてください')
        navigate('/login')
      } else if (code === 'missing-user' || code === 'missing-email') {
        showErrorMessage('認証情報が見つかりません。再度ログインしてください。')
        navigate('/login')
      } else {
        const errorMsg = 'パスワードの更新に失敗しました。もう一度お試しください'
        showErrorMessage(errorMsg)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const toggleInitialPasswordVisibility = () => {
    setShowInitialPassword(!showInitialPassword)
  }

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  return {
    initialPassword,
    setInitialPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    showInitialPassword,
    showNewPassword,
    showConfirmPassword,
    isLoading,
    initialPasswordError,
    newPasswordError,
    confirmPasswordError,
    setInitialPasswordError,
    setNewPasswordError,
    setConfirmPasswordError,
    handleSubmit,
    toggleInitialPasswordVisibility,
    toggleNewPasswordVisibility,
    toggleConfirmPasswordVisibility,
  }
}
